import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import OperationList from "./operationList/OperationList";
import Header from "./Header";
import ExtractedInfo from "./ExtractedInfo";
import BottomBar from "./BottomBar";
import { useAutoCreateOperations, useFetchPart } from "@/lib/queries/hooks";
import FileDisplay from "./FileDisplay";
import OperationListContext from "./operationList/lib/OperationListContext";
import { ScrollSync } from "react-scroll-sync";
import HeaderRow from "./operationList/HeaderRow";
import FooterRow from "./operationList/FooterRow";
import classNames from "classnames";
import { useEffect } from "react";
import { useParams } from "react-router";
import PartNote from "@/lib/PartNote";

const engineeringDrawingWidthPercentage = 57;

export default function Part() {
  const { data: part } = useFetchPart();
  const { teamSlug } = useParams();
  const { mutate: autoCreateOperations, isPending } = useAutoCreateOperations();

  useEffect(() => {
    if (part?.should_create_operations_jit) {
      autoCreateOperations({
        params: {
          path: {
            team_slug: teamSlug!,
            id: part.id,
          },
        },
      });
    }
  }, [part, autoCreateOperations, teamSlug]);

  if (!part) return null;

  const { requested_quantities: quantities, will_bid } = part;

  const materialsOrParts = part.operations.filter(
    (o) => o.general_type === "material",
  );
  const operations = part.operations.filter(
    (o) => o.general_type === "operation",
  );

  return (
    <div className="h-full flex flex-col">
      <PanelGroup direction="horizontal" autoSaveId="persistence">
        <Panel defaultSize={100 - engineeringDrawingWidthPercentage}>
          <div className="h-full flex flex-col gap-1 px-4 pt-2">
            <Header part={part} />
            <ExtractedInfo />

            <OperationListContext.Provider value={{ quantities }}>
              <ScrollSync>
                <div
                  className={classNames(
                    "flex grow flex-col gap-2 overflow-y-auto outline-5 outline-transparent m-1",
                    {
                      "!outline-red-500 p-1": will_bid === false,
                      "!outline-green-500 p-1": will_bid === true,
                    },
                  )}
                >
                  <HeaderRow
                    part={part}
                    preventEdit={!!part.parent || will_bid !== null}
                    calculatorCreationInProgress={isPending}
                  />
                  <OperationList
                    title="Materials & Parts"
                    operations={materialsOrParts}
                    quantities={quantities}
                    generalCalculatorType="material"
                    canEditPart={will_bid === null}
                    subParts={part.children}
                  />
                  <OperationList
                    title="Operations"
                    operations={operations}
                    quantities={quantities}
                    generalCalculatorType="operation"
                    canEditPart={will_bid === null}
                    subParts={[]}
                  />
                  <PartNote part={part} quantities={quantities} />
                  <FooterRow
                    operations={part.operations.concat(
                      part.children.flatMap((c) => c.operations),
                    )}
                    left={<div />}
                  />
                </div>
              </ScrollSync>
            </OperationListContext.Provider>

            <BottomBar part={part} />
          </div>
        </Panel>
        <PanelResizeHandle className="w-0.25 bg-slate-300" />
        <Panel
          defaultSize={engineeringDrawingWidthPercentage}
          minSize={20}
          maxSize={80}
        >
          <FileDisplay key={part.id} />
        </Panel>
      </PanelGroup>
    </div>
  );
}
